import { Component, inject, input, signal } from "@angular/core";
import { IconComponent } from "@shared/components/atoms/icon/icon.component";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { debounceTime, switchMap } from "rxjs/operators";
import { ProductsService } from "@shared/services/products.service";
import { NgOptimizedImage, NgTemplateOutlet } from "@angular/common";
import { ProductPriceComponent } from "@shared/components/atoms/product-price/product-price.component";
import { filter, map, merge, of, Subject } from "rxjs";
import { TranslatePipe } from "@ngx-translate/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { ProductStatusComponent } from "@shared/components/atoms/product-status/product-status.component";
import { SmallestThumbnailPipe } from "@shared/pipes/smallest-thumbnail.pipe";
import { LoadingSpinnerComponent } from "@shared/components/atoms/loading-spinner/loading-spinner.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ClickOutsideDirective } from "@shared/directives/click-outside.directive";
import type { Product } from "@ophardt/shopware-api";
import { BreakpointService } from "@core/services/breakpoint.service";

@Component({
  selector: "app-search-bar",
  imports: [
    IconComponent,
    ReactiveFormsModule,
    NgOptimizedImage,
    ProductPriceComponent,
    RouterLink,
    TranslatePipe,
    ProductStatusComponent,
    SmallestThumbnailPipe,
    LoadingSpinnerComponent,
    NgTemplateOutlet,
    ClickOutsideDirective,
  ],
  providers: [ProductsService],
  templateUrl: "./search-bar.component.html",
  styleUrl: "./search-bar.component.scss",
})
export class SearchBarComponent {
  placeholder = input.required<string>();

  private readonly productsService = inject(ProductsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly breakpointService = inject(BreakpointService);

  readonly isDesktop = this.breakpointService.isDesktop;

  readonly searchControl = new FormControl<string>("");
  readonly isShowSuggestionBox = signal<boolean>(false);

  private readonly focusEvent$ = new Subject<void>();
  private readonly controlValueChangeEvent$ = this.searchControl.valueChanges.pipe(debounceTime(300));
  readonly suggestedProducts = signal<Product[] | null>(null);

  constructor() {
    this.initSearchResultListener();
    this.listenSearchQueryParam();
    this.listenSearchControlChanges();
  }

  toggleSuggestionBox(isShow?: boolean): void {
    this.isShowSuggestionBox.set(isShow ?? false);
  }

  clearSearchInput(): void {
    this.searchControl.setValue("");
  }

  onSearchEnter(): void {
    const searchValue = this.searchControl.value?.trim();
    if (searchValue) {
      void this.router.navigate(["/products"], {
        queryParams: { search: searchValue },
        queryParamsHandling: "merge",
      });

      this.toggleSuggestionBox(false);
    }
  }

  private initSearchResultListener(): void {
    merge(this.controlValueChangeEvent$, this.focusEvent$)
      .pipe(
        switchMap(() => {
          if (this.searchControl.value) {
            return this.productsService.getSearchProductsByTerm(this.searchControl.value);
          } else {
            return of(null);
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe((products) => {
        this.suggestedProducts.set(products);
      });
  }

  private removeSearchQueryParam(): void {
    const currentParams = { ...this.route.snapshot.queryParams };

    if (currentParams["search"]) {
      delete currentParams["search"];
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: currentParams,
        queryParamsHandling: null,
      });
    }
  }

  private listenSearchQueryParam(): void {
    this.route.queryParamMap
      .pipe(
        map((params) => params.get("search")),
        filter(Boolean),
        takeUntilDestroyed(),
      )
      .subscribe((searchValue) => {
        this.searchControl.patchValue(searchValue);
      });
  }

  private listenSearchControlChanges(): void {
    this.searchControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (!value) {
        this.removeSearchQueryParam();
      }
    });
  }
}
